<template>
  <div
    v-on-click-outside="onClickOutside"
    :class="['custom-select', selectStyle, { open }]"
  >
    <div class="custom-select-sort" tabindex="0" @click="onHeaderClick">
      <slot name="header" :selected="selected" :open="open">
        <span :class="['label-sort', fontClass]">{{
          selected[nameParam]
        }}</span>
      </slot>
    </div>
    <Teleport to="body" :disabled="!isMobile">
      <div
        ref="listBody"
        :class="['custom-select-list-wrapper', { open }]"
        :style="{ 'min-width': listWidth ? listWidth + 'px' : '' }"
        @click="onPopup(false)"
      >
        <perfect-scrollbar
          class="custom-select-list"
          :options="{ wheelPropagation: false }"
          @click.stop
        >
          <div :class="['popup-title', fontClass]">
            {{ placeholder }}
            <div class="icon-close" @click="onPopup(false)" />
          </div>
          <div
            v-for="(item, index) in list"
            :key="index[valueParam]"
            :class="[
              'select-item',
              { active: selected && item[valueParam] === selected[valueParam] },
            ]"
            @click="onSelect(item)"
          >
            <slot
              name="item"
              :item="item"
              :active="selected && item[valueParam] === selected[valueParam]"
            >
              <span :class="['item-title', fontClass]">{{
                item[nameParam]
              }}</span>
            </slot>
          </div>
        </perfect-scrollbar>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { vOnClickOutside } from '@vueuse/components';
import { useScrollLock } from '@vueuse/core';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import type { IBuiltInMemory, PSelect } from '~/interfaces/app';

const props = defineProps({
  list: {
    type: Array,
    required: true,
  },
  value: {
    type: String,
    Number,
    required: true,
  },
  valueParam: {
    type: String,
    default: 'id',
  },
  nameParam: {
    type: String,
    default: 'name',
  },
  placeholder: {
    type: String,
    default: '',
  },
  selectStyle: {
    type: String,
    default: '',
    validator: (value: string) => ['', 'text', 'standard'].includes(value),
  },
  listWidth: {
    type: Number,
    default: 0,
  },
  fontClass: {
    type: String,
    default: 'body-m-regular',
  },
});
const emit = defineEmits(['change', 'open', 'close']);
const valueParam = toRef(props, 'valueParam');
const open = ref<boolean>(false);
const isMobile = computed(
  () =>
    mediaStore.isMobile || mediaStore.isEs || mediaStore.isSm || mediaStore.isMd
);

const mediaStore = useMediaQueryStore();

const listBody = ref<HTMLElement>();
const html = ref<HTMLElement | null>(null);
const isLocked = useScrollLock(html);

const isListObjects = computed(
  () => props.list && props.list.length > 0 && typeof props.list[0] === 'object'
);

const entryList = computed(() =>
  isListObjects.value
    ? props.list
    : props.list.map((item, index) => ({ id: index, title: item }))
);

const selected = computed(
  () =>
    entryList.value.find((item: string[] | IBuiltInMemory | any) => {
      return isListObjects.value
        ? item[props.valueParam] === props.value
        : item[props.nameParam] === props.value;
    }) || {}
);

watch(
  () => isMobile.value,
  (v) => {
    onPopup(false);
    if (!v) {
      isLocked.value = false;
    }
  }
);
watch(
  () => open.value,
  (v) => {
    if (isMobile.value) {
      isLocked.value = v;
    }
    if (v) {
      emit('open');
    } else {
      emit('close');
    }
  }
);
onMounted(() => {
  html.value = document.querySelector('html');
});

function onPopup(state: boolean): void {
  if (state) {
    open.value = true;
    nextTick(() => {
      if (listBody.value) {
        const scrollWidth =
          listBody.value?.offsetWidth - listBody.value?.clientWidth;
        document.body.classList.add('tablet-overflow-hidden-grid');
        document.body.style.paddingRight = scrollWidth + 'px';
      }
    });
  } else {
    open.value = false;
    document.body.classList.remove('tablet-overflow-hidden-grid');
    document.body.style.paddingRight = '';
  }
}

function onHeaderClick(): void {
  if (isMobile.value) onPopup(true);
  else open.value = !open.value;
}

function onClickOutside(): void {
  if (!isMobile.value) open.value = false;
}

function onSelect(item: PSelect) {
  onPopup(false);
  const vParam = props.valueParam as keyof typeof selected.value;
  if (selected.value[vParam] !== item[vParam])
    emit('change', isListObjects.value ? item[vParam] : item[vParam]);
}
</script>

<style scoped lang="scss">
.custom-select {
  position: relative;
  width: auto;
  display: flex;
  flex-flow: column nowrap;

  &-sort {
    display: block;
    width: auto;
    cursor: pointer;
    user-select: none;
    outline: none;
    box-shadow: none;
    position: relative;
  }

  &-list-wrapper {
    overflow: hidden;
    align-self: center;
    opacity: 1;
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0);
    width: 100vw;
    transition: height 0s 0.2s, background-color 0.2s;
    z-index: 100;
    overflow-y: scroll;
    height: 0;

    @include lg-block {
      opacity: 0;
      position: absolute;
      width: 100%;
      height: 0;
      bottom: initial;
      top: 100%;
      background-color: transparent;
      overflow-y: scroll;
      transition: opacity 0.2s;
    }

    .custom-select-list {
      background-color: $white;
      user-select: none;
      display: flex;
      flex-flow: column nowrap;
      width: 100%;
      max-height: 100%;
      border: none;
      border-radius: unset;
      box-shadow: none;
      padding: 0 12px 12px;

      @include md-block {
        padding: 0 20px 20px;
        transform: translateY(100%);
        transition: transform 0.2s;
      }

      @include lg-block {
        max-height: 300px;
        border: 1px solid $achromatic-hover;
        border-radius: 0;
        padding: 8px;
        transform: none;
      }

      .select-item {
        cursor: pointer;
      }

      .popup-title {
        justify-content: space-between;
        align-items: center;
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: $white;
        padding: 0 0 10px 0;
        margin-top: 4px;
        display: flex;

        @include lg-block {
          margin-top: 8px;
          padding: 12px 0;
          display: none;
        }
      }

      .icon-close {
        width: 20px;
        height: 20px;
        cursor: pointer;
        mask: url('/static/images/closeModal.svg') center / cover no-repeat;
        background-color: $achromatic-black;

        &:hover {
          background-color: $main-cta;
        }

        &:active {
          background-color: $hover-cta;
        }
      }
    }
  }

  &.standard,
  &.text {
    padding: 0;
    width: auto;

    .custom-select-sort {
      display: flex;
      align-items: center;
      color: $achromatic-black;
      justify-content: space-between;
      padding: 8px 12px;
      border-radius: 8px;
      background: #f5f5f5;

      &:active {
        background-color: $light-blue;
      }

      &:hover {
        @include lg-block {
          background-color: $light-blue;
        }
      }

      &:after {
        content: '';
        display: block;
        mask: url('/static/images/icon/triangle-filter.svg') center / contain
          no-repeat;
        background-color: $achromatic-black;
        flex-shrink: 0;
        transform: translateY(-1px);
        transition: transform 0.3s ease-in;
        transition-property: transform;
        transition-duration: 0.3s;
        transition-timing-function: ease-in;
        transition-delay: 0s;
        margin-left: 4px;
        width: 16px;
        height: 16px;
        @include lg-block {
          width: 20px;
          height: 20px;
          margin-left: 4px;
        }
      }

      .label-sort {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .custom-select-list-wrapper {
      background-color: $achromatic-card-bg;

      .custom-select-list {
        .select-item {
          color: $achromatic-black;
          padding: 12px 0;

          &:nth-child(2) {
            padding-top: 4px;
            border-top: none;

            @include md-block {
              padding-top: 7px;
            }
          }

          &.active {
            justify-content: space-between;
          }

          .item-title {
            overflow-wrap: break-word;
          }
        }
      }

      .active {
        justify-content: space-between;
      }
    }

    &.open {
      .custom-select-sort {
        &:after {
          transform: rotate(180deg) translateY(0px);
        }
      }

      .custom-select-list-wrapper {
        background-color: rgba(0, 0, 0, 0.6);
        transition: background-color 0.2s;
        top: 0;
        left: 0;
        padding-top: 98px;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: stretch;
        overflow: initial;
        height: var(--fullvh);
        @include lg-block {
          background-color: rgba(0, 0, 0, 0);
          padding-top: 0;
          top: 100%;
          overflow: visible;
          height: auto;
          opacity: 1;
          z-index: 40;
        }
      }
    }
  }

  &.standard {
    .custom-select-sort {
      border: 1px solid $achromatic-hover;
      border-radius: 4px;
      padding: 17px 15px;

      &:active,
      &:hover {
        border: 1px solid $achromatic-disabled;
      }
    }

    .custom-select-list-wrapper {
      .custom-select-list {
        .select-item {
          padding: 8px;

          &:nth-child(2) {
            &.active + .select-item {
              border-top: none;
              padding-top: 7px;

              @include md-block {
                padding-top: 4px;
              }
            }
          }

          &.active {
            display: none;
          }

          &:hover {
            background-color: initial;
          }
        }
      }
    }

    &.open {
      .custom-select-sort {
        border: 1px solid $achromatic-black;
      }
    }
  }

  &.text {
    .custom-select-list-wrapper {
      .custom-select-list {
        padding: 12px;
        border-radius: 4px;
        @include lg-block {
          padding: 8px 0 4px;
        }

        .select-item {
          padding: 6px 0;
          border-radius: 4px;
          margin-bottom: 4px;

          @include lg-block {
            padding: 6px 12px;
          }

          &.active {
            display: flex;
            align-items: center;

            &:after {
              content: '';
              display: block;
              mask: url('/static/images/icon/selected-check.svg') center /
                contain no-repeat;
              background-color: $main-cta;
              width: 16px;
              height: 16px;
              flex-shrink: 0;
              margin-left: 12px;
              margin-bottom: 2px;

              @include lg-block {
                margin-left: 12px;
              }
            }
          }

          &:hover {
            background-color: initial;

            @include lg-block {
              background-color: $light-blue;
            }
          }
        }
      }
    }

    &.open {
      .custom-select-sort {
        color: $achromatic-black;
        justify-content: space-between;

        &:after {
          background-color: $achromatic-black;
        }
      }
    }
  }
}

.custom-select-list-wrapper {
  visibility: hidden;

  .custom-select-list {
    transform: translateY(100%);
    transition: none;
  }

  &.open {
    visibility: visible;
    height: var(--fullvh);
    background-color: rgba(0, 0, 0, 0.6);
    transition: background-color 0.2s;
    top: 0;
    left: 0;
    padding-top: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: stretch;
    overflow: initial;

    .custom-select-list {
      position: absolute;
      bottom: 0;
      left: 0;
      transform: translateY(0);
      transition: transform 0.2s, visibility 0.2s;
      box-shadow: 0 9px 40px rgba(0, 0, 0, 0.08);
      padding: 8px;
      border: 1px solid #e7e7e7 !important;
      border-radius: 4px;

      .select-item {
        color: $achromatic-black;
        padding: 4px 2px 4px 0;
        justify-content: space-between;
        align-items: center;
        display: flex;
        border-top: none;
        border-radius: 4px;
        margin-bottom: 4px;
        cursor: pointer;

        .item-title {
          overflow-wrap: break-word;
          font-size: 16px;
          line-height: 20px;
        }

        &:nth-child(2) {
          padding-top: 4px;
          border-top: none;

          @include md-block {
            padding-top: 7px;
          }
        }

        &.active {
          &:after {
            background-color: $main-cta;
            content: '';
            display: block;
            flex-shrink: 0;
            height: 16px;
            margin-bottom: 2px;
            margin-left: 12px;
            mask: url('/static/images/icon/selected-check.svg') center / contain
              no-repeat;
            width: 16px;
          }
        }
      }

      @include lg-block {
        position: initial;
        transition: unset;
        padding: 0;
        top: -2px;
        box-shadow: 0 8px 16px rgba(51, 51, 51, 0.2);
        border: 1px solid $achromatic-hover !important;
      }
    }
  }

  @include lg-block {
    background-color: rgba(0, 0, 0, 0.6);
    transition: background-color 0.2s;
    right: 0;
    left: 0;
    bottom: unset;
    top: 0;
    padding-top: 98px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: stretch;
  }
}
</style>
